import { defer, Observable, of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";

import { Injectable } from "@angular/core";

import { AssetType } from "@rvi/asset-modules";
import { IPC_CHANNEL } from "../../_offline/electron.constants";
import { SubjectModelLocationsSchema } from "../../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema, SubjectModelSchemaQueryOpt } from "../../_offline/models/subjectModel.schema";
import { ElectronService } from "../../electron.service";
import { AssetModuleClient } from "../asset-module/asset-module-client.service";
import { DatabaseBaseService } from "../base/database-base.service";
import { FeatureFlagOfflineService } from "../feature-flag/feature-flag.offline.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { LoginUserOfflineService } from "../offline/login-user-offline.service";
import { STANDALONE } from "../../_offline/standalone-data/standalone-data";

@Injectable({
  providedIn: "root"
})
export class SubjectModelDbService extends DatabaseBaseService {
  constructor(
    protected electronService: ElectronService,
    protected loginUserService: LoginUserOfflineService,
    private featureFlagService: FeatureFlagOfflineService,
    private assetModuleClient: AssetModuleClient,
    private fileSystemAccessService: FileSystemAccessService
  ) {
    super(electronService, loginUserService);
  }

  getAll(enabledOnly = true, aliveOnly = true): Observable<SubjectModelSchema[]> {
    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      return this.assetModuleClient.getAssetTypes().pipe(
        map((assetTypes) => {
          const filtered = enabledOnly ? assetTypes.items.filter((at) => at.isEnabled) : assetTypes.items;
          return filtered.map((at) => this.toSubjectModelSchema(at));
        })
      );
    }
    const opt = new SubjectModelSchemaQueryOpt(this.tenantId, enabledOnly, aliveOnly);
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.GET_ALL, opt);
  }

  private toSubjectModelSchema(assetType: AssetType): SubjectModelSchema {
    const schema = new SubjectModelSchema();
    schema.id = assetType.id;
    schema.name = assetType.name;
    schema.isValid = assetType.isEnabled;
    schema.tenantId = this.tenantId;
    schema.lastUpdateDate = assetType.lastUpdate ? new Date(assetType.lastUpdate).toISOString() : null;
    schema.structImageFilePath = assetType.structureImagePath ?? null;
    schema.isDeleted = false;
    schema.isOfflineData = false;
    schema.subjects = [];
    schema.subjectModelLocations = [];
    return schema;
  }

  // Needed to get the subject model from the legacy version even if the feature flag for asset module is activated
  getAllOnPremise(): Observable<SubjectModelSchema[]> {
    const opt = new SubjectModelSchemaQueryOpt(STANDALONE.TENANT_ID, true, true);
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.GET_ALL, opt);
  }

  putAll(subjectModels: SubjectModelSchema[]): Observable<SubjectModelSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.PUT_ALL, subjectModels);
  }

  getOne(id: string, enabledOnly = true, aliveOnly?: boolean): Observable<SubjectModelSchema> {
    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      return this.assetModuleClient.getLiveAsset(id, this.tenantId).pipe(map((s) => s.subjectModel));
    }
    // With the Legacy SubjectModel, the struct image file path is stored as a raw file system path, which cannot be used
    // directly in the browser. It must be converted to a local URL (e.g. asset:// or file://)
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.GET_ONE, this.tenantId, id, enabledOnly, aliveOnly).pipe(
      mergeMap((schema: SubjectModelSchema) => {
        if (!schema.structImageFilePath) return of(schema);
        return defer(() =>
          this.fileSystemAccessService
            .resolveLocalFileUrl(schema.structImageFilePath)
            .then((url) => {
              schema.structImageFilePath = url;
              return schema;
            })
            .catch(() => schema)
        );
      })
    );
  }

  /**
   * 検査テンプレートを追加、更新する。
   *
   * @param subjectModel SubjectModelSchema。
   * @returns 更新結果を返す。
   */
  putOne(subjectModel: SubjectModelSchema): Observable<SubjectModelSchema> {
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.PUT_ONE, subjectModel).pipe(map((x) => x as SubjectModelSchema));
  }

  /**
   * 同じ Subject Model 名が存在するかどうかを返す。
   *
   * @param tenantId テナント ID。
   * @param subjectModelName Subject model 名。
   * @param subjectModelId Subject model ID。指定された Subject model ID は除外して検索する。空文字の場合は検索条件に含めない。
   */
  public isSameSubjectModelNameExists(tenantId: string, subjectModelName: string, subjectModelId: string = ""): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.IS_SAME_SUBJECT_MODEL_NAME_EXISTS, tenantId, subjectModelName, subjectModelId).pipe(
      map((x) => x as boolean)
    );
  }

  /**
   * インポートした検査テンプレート(SubjectModel、 SubjectModelLocations)を Insert する。
   *
   * @param subjectModel SubjectModel。
   * @param subjectModelLocations SubjectModelLocations。
   * @returns 成功した場合は true を返す。
   */
  public putImportedSubjectModel(subjectModel: SubjectModelSchema, subjectModelLocations: SubjectModelLocationsSchema): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.PUT_IMPORTED_SUBJECT_MODEL, subjectModel, subjectModelLocations).pipe(map((x) => x as boolean));
  }

  /**
   * インポートした検査テンプレートの画像情報を更新する。
   *
   * @param subjectModel SubjectModel。
   * @param base64ImageData base64 にした画像データ。
   * @param imageFileFullPath 画像データのフルパス。
   * @param mimeType MIME type。
   * @returns 成功した場合は true を返す。
   */
  public putImportedSubjectModelImage(
    subjectModel: SubjectModelSchema,
    base64ImageData: string,
    imageFileFullPath: string,
    mimeType: string
  ): Observable<boolean> {
    return this.ipcSendSync(
      IPC_CHANNEL.SUBJECT_MODEL.PUT_IMPORTED_SUBJECT_MODEL_IMAGE,
      subjectModel,
      base64ImageData,
      imageFileFullPath,
      mimeType
    ).pipe(map((x) => x as boolean));
  }

  public delete(subjectModelId: string, blobsPath: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.DELETE, subjectModelId, blobsPath).pipe(map((x) => x as boolean));
  }

  hasInspections(subjectModelId: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.SUBJECT_MODEL.HAS_INSPECTIONS, subjectModelId).pipe(map((x) => x as boolean));
  }
}
